/**
 * Journey A — Reusable teaching empty state.
 *
 * Replaces bare/blank "no data" branches with a friendly, instructive panel:
 *   icon + one-line headline + one-sentence "what to do" + a primary action.
 *
 * Built in the legacy SCSS style (no MUI). The action can be either:
 *   - an in-app route (`actionTo`) — rendered as a react-router <Link>, OR
 *   - an in-page handler (`onAction`) — rendered as a <button>.
 * Pass exactly one. When neither is supplied the panel renders without a CTA
 * (useful for purely informational empty states).
 *
 * Icons are the shared custom SVG elements from `lib/allCustomSVGs` — pass the
 * element itself (e.g. `shopSVG`); it is rendered through `CustomSVGs` so the
 * existing fill/stroke conventions apply.
 */
import { Link } from 'react-router-dom';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import '../../styles/components/emptyState/teachingEmptyState.scss';

interface Props {
  /** A custom SVG element from `lib/allCustomSVGs` (e.g. `shopSVG`). */
  icon?: any;
  /** One-line headline. */
  title: string;
  /** One-sentence "what to do". */
  body: string;
  /** Primary action label. Omit to render no CTA. */
  actionLabel?: string;
  /** In-app route for the primary action (rendered as a <Link>). */
  actionTo?: string;
  /** In-page handler for the primary action (rendered as a <button>). */
  onAction?: () => void;
  /** Visual size — `compact` trims padding for use inside cards. */
  size?: 'default' | 'compact';
}

const TeachingEmptyState = ({
  icon,
  title,
  body,
  actionLabel,
  actionTo,
  onAction,
  size = 'default',
}: Props) => {
  const showAction = Boolean(actionLabel && (actionTo || onAction));

  return (
    <div
      className={`teachingEmptyState${
        size === 'compact' ? ' teachingEmptyState--compact' : ''
      }`}
    >
      {icon && (
        <div className='teachingEmptyState__icon' aria-hidden='true'>
          <CustomSVGs svg={icon} />
        </div>
      )}
      <h3 className='teachingEmptyState__title'>{title}</h3>
      <p className='teachingEmptyState__body'>{body}</p>
      {showAction &&
        (actionTo ? (
          <Link to={actionTo} className='teachingEmptyState__action'>
            {actionLabel}
          </Link>
        ) : (
          <button
            type='button'
            className='teachingEmptyState__action'
            onClick={onAction}
          >
            {actionLabel}
          </button>
        ))}
    </div>
  );
};

export default TeachingEmptyState;
